import Raven from 'raven-js'
import moment from 'lib/utils/moment'
import { APP_VERSION } from '../../config'
import ErrorTracker from '../error-tracker'
import {
  trackAppCrash,
  TRANSACTIONAL_CRASH,
  JAVASCRIPT_CRASH,
  GRAPHQL_CRASH,
  SLOTH_CRASH
} from './stats'

async function trackWithAJS(error, options = {}) {
  const payload = await ErrorTracker.extractErrorInfo(error, options)
  ErrorTracker.logFullStory('JavaScript Error')
  window.analytics.track('JavaScript Error', payload)

  // Metric via statsd proxy
  trackAppCrash({
    type: JAVASCRIPT_CRASH,
    route: payload.route,
    team: payload.team
  })
}

// @see https://docs.getsentry.com/hosted/clients/javascript/usage/#raven-js-reporting-errors
export async function track(err, options = {}) {
  // don't let raven cause more exceptions...
  try {
    Raven.captureException(err, options)
    await trackWithAJS(err, options)
    console.error(err)
  } catch (e) {
    console.error(e)
  }
}

export async function trackSlothOfDeath(err, extraContext = {}) {
  try {
    const errorInfo = await ErrorTracker.extractErrorInfo(err)
    const payload = {
      ...extraContext,
      ...errorInfo
    }
    window.analytics.track('Sloth Of Death', payload)
    ErrorTracker.logFullStory('Sloth of Death')

    // Metric via statsd proxy
    trackAppCrash({
      type: SLOTH_CRASH,
      route: errorInfo.route,
      team: errorInfo.team
    })
  } catch (e) {
    console.error(e)
  }
}

/*
  Network Errors are errors that occurr at Network interface
  layer that connects the App to Gateway-API
*/
export function trackNetworkError(networkError, operation = '') {
  ErrorTracker.extractErrorInfo(networkError)
    .then(errorInfo => {
      const payload = {
        operation,
        statusCode: networkError.statusCode,
        ...errorInfo
      }

      window.analytics.track('Network Error', payload)
      ErrorTracker.logFullStory('Network Error')
    })
    .catch(error => console.error(error))
}

/*
  GraphQL errors are usually validation errors or common invalid paths
  usually related to invalid requests to GraphQL.
*/
export function trackGraphQLErrors(graphqlErrors, operation = '') {
  try {
    const timezone = moment.tz.guess()
    const sentryId = Raven.lastEventId()
    const fullStory = ErrorTracker.getFullStory()
    const route = ErrorTracker.currentRoute
    const team = ErrorTracker.currentTeam
    const appVersion = APP_VERSION

    const payload = {
      fullStory,
      timezone,
      sentryId,
      route,
      team,
      operation,
      graphqlErrors,
      appVersion
    }

    // Log the errors individually so the objects are expanded by default in the devtools
    for (const graphqlError of graphqlErrors) {
      console.error('GraphQL Error', operation, graphqlError)
    }

    ErrorTracker.logFullStory('GraphQL Error')
    window.analytics.track('GraphQL Error', payload)

    // Metric via statsd proxy
    trackAppCrash({
      type: GRAPHQL_CRASH,
      componentName: operation,
      route,
      team
    })
  } catch (e) {
    console.error(e)
  }
}

/*
  Transactional errors are errors our users make that prevent them from
  completing a task.

  These are not necessarily crashes, but moments of frustration when
  users attempted to perform an invalid task or were prevent from finishing
  that task by a server error/crash.
*/
export function trackTransactionalError(displayName = '', props = {}) {
  try {
    const timezone = moment.tz.guess()
    const sentryId = Raven.lastEventId()
    const failedGraphQLQueries = ErrorTracker.failedApolloQueries()
    const fullStory = ErrorTracker.getFullStory()
    const route = ErrorTracker.currentRoute
    const team = ErrorTracker.currentTeam
    const appVersion = APP_VERSION

    const payload = {
      displayName,
      fullStory,
      timezone,
      sentryId,
      failedGraphQLQueries,
      route,
      team,
      appVersion,
      ...props
    }

    ErrorTracker.logFullStory('Transactional Error')
    window.analytics.track('Transactional Error', payload)

    // Metric via statsd proxy
    trackAppCrash({
      type: TRANSACTIONAL_CRASH,
      componentName: displayName,
      route,
      team
    })
  } catch (e) {
    console.error(e)
  }
}
